import Tree from 'components/tree/Tree';
import {
  Alert,
  Button,
  Flex,
  Form,
  Input,
  Modal,
  Select,
  Tabs,
  Tag
} from 'antd';
import { ClassroomsDisciplineUpdateModal } from './components/ClassroomsDisciplineUpdate';
import {
  CopyOutlined,
  HistoryOutlined,
  UnorderedListOutlined
} from '@ant-design/icons';
import { CoursesIconComponent } from 'assets/icons/courses';
import { EditLayout } from 'components/layout/layout/EditLayout';
import { IClassroomUpdateStatus } from '../../../interfaces/classrooms';
import {
  IGraduateDisciplineModules,
  IGraduateDisciplineShow,
  IGraduateDisciplineTree
} from 'interfaces/graduate_disciplines';
import { PageHeader } from 'components/professor_area/PageHeader';
import { TabClassrooms } from './components/TabClassrooms';
import { Typography } from 'antd/lib';
import { UpdateProgressModal } from './components/UpdateProgress';
import { getSelectOptionWithDefault } from 'utils/get-select-options-with-default';
import { getTenant } from 'services/tenants';
import { removeDuplicateObjectsById } from 'utils/remove-duplicate-objects';
import {
  useApiUrl,
  useCan,
  useCustom,
  useCustomMutation
} from '@refinedev/core';
import { useEffect, useMemo, useState } from 'react';
import { useForm, useSelect } from '@refinedev/antd';
import { useGraduateDisciplineStore } from './graduate_disciplines.store';
import { useNavigate, useParams } from 'react-router-dom';

export const GraduateDisciplineEdit = () => {
  const [classroomsUpdateStatus, setClassroomsUpdateStatus] =
    useState<IClassroomUpdateStatus>({} as IClassroomUpdateStatus);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [progressModalOpen, setProgressModalOpen] = useState(false);
  const [cloneModalOpen, setCloneModalOpen] = useState(false);
  const [isCloning, setIsCloning] = useState(false);

  const { id: disciplineId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const apiUrl = useApiUrl();

  const { name } = getTenant();
  const tenantPrefix = name?.slice(0, 3).toUpperCase() || '';
  const [activeTab, setActiveTab] = useState('info');
  const { form, formProps, saveButtonProps, queryResult } =
    useForm<IGraduateDisciplineShow>({
      redirect: 'edit'
    });
  const [cloneForm] = Form.useForm<{ code: string; name?: string }>();
  const { data: canEdit } = useCan({
    resource: 'graduate_disciplines',
    action: 'edit'
  });
  const { data: canClone } = useCan({
    resource: 'graduate_disciplines',
    action: 'create'
  });
  const { data: canListClassrooms } = useCan({
    resource: 'classrooms',
    action: 'list'
  });
  const { mutate: cloneDiscipline } =
    useCustomMutation<IGraduateDisciplineShow>();
  const [contentTree, setContentTree] = useState<IGraduateDisciplineTree>();

  const discipline = queryResult?.data?.data;

  const setDiscipline = useGraduateDisciplineStore(s => s.setDiscipline);

  useEffect(() => {
    if (discipline) {
      setDiscipline(discipline);
    }
  }, [discipline, setDiscipline]);

  const { refetch: checkUpdateStatus } = useCustom<IClassroomUpdateStatus>({
    url: `admin/graduate/disciplines/${disciplineId}/sync_content`,
    method: 'get',
    queryOptions: {
      enabled: false
    }
  });

  useEffect(() => {
    if (classroomsUpdateStatus.done) {
      return;
    }
    // eslint-disable-next-line prefer-const
    let interval: NodeJS.Timeout;

    const poll = async () => {
      const { data } = await checkUpdateStatus();
      data?.data && setClassroomsUpdateStatus(data.data);
      const isDone = data?.data.done;

      if (isDone || data?.data.total === 0) {
        clearInterval(interval);
      }
    };
    poll();
    interval = setInterval(poll, 3000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disciplineId, classroomsUpdateStatus.done, progressModalOpen]);

  const moduleIds = queryResult?.data?.data.modules.map(e => e.id);

  const {
    selectProps: modulesSelectProps,
    queryResult: modulesQueryResult,
    defaultValueQueryResult
  } = useSelect<IGraduateDisciplineModules>({
    resource: 'graduate_modules',
    optionLabel: 'name',
    optionValue: 'id',
    defaultValue: moduleIds,

    onSearch: value => [
      {
        field: 'filter[q]',
        operator: 'eq',
        value
      }
    ],

    pagination: {
      mode: 'server'
    }
  });

  const modules = useMemo(() => {
    const modulesWithDefault = getSelectOptionWithDefault(
      defaultValueQueryResult,
      modulesQueryResult
    );

    if (modulesWithDefault) {
      const mods = removeDuplicateObjectsById(modulesWithDefault);

      return mods?.map(module => {
        const {
          external_reference,
          name,
          course_external_reference,
          course_name,
          id
        } = module;

        const codModuleExternal = external_reference
          ? `${external_reference} - `
          : '';

        const codCourseExternal = course_external_reference
          ? `${course_external_reference} - `
          : '';

        const label = `Módulo: ${codModuleExternal} ${name} | Curso: ${codCourseExternal} ${course_name}`;

        return {
          label,
          value: id
        };
      });
    }
  }, [defaultValueQueryResult, modulesQueryResult]);

  useEffect(() => {
    if (discipline) {
      setContentTree(discipline.content_tree);
    }
  }, [discipline]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onFinish = (data: any) => {
    formProps.onFinish!({
      ...data,
      content_tree: contentTree,
      code: name?.slice(0, 3).toUpperCase() + form.getFieldValue('code')
    });
  };

  const onUpdateTree = (data: IGraduateDisciplineTree) => {
    setContentTree(data);
  };

  const handleClone = (values: { code: string; name?: string }) => {
    if (!disciplineId) {
      return;
    }

    setIsCloning(true);
    cloneDiscipline(
      {
        method: 'post',
        url: `${apiUrl}/graduate_disciplines/${disciplineId}/clone`,
        values: {
          code: `${tenantPrefix}${values.code}`,
          name: values.name
        },
        successNotification: {
          type: 'success',
          message: 'Disciplina clonada com sucesso'
        },
        errorNotification: {
          type: 'error',
          message: 'Erro ao clonar disciplina'
        }
      },
      {
        onSuccess: response => {
          const clonedDisciplineId = response?.data?.id;

          setCloneModalOpen(false);
          cloneForm.resetFields();
          setIsCloning(false);

          if (clonedDisciplineId) {
            navigate(
              `/course_creation/graduate_disciplines/edit/${clonedDisciplineId}`
            );
          }
        },
        onError: () => {
          setIsCloning(false);
        }
      }
    );
  };

  return (
    <EditLayout
      title="Editar disciplina"
      footerButtonProps={{
        style: {
          justifyContent: 'flex-end',
          display:
            ['info', 'tree'].includes(activeTab) && canEdit?.can
              ? 'flex'
              : 'none'
        }
      }}
      saveButtonProps={canEdit?.can ? saveButtonProps : { hidden: true }}
    >
      <PageHeader
        title={`Disciplina: ${discipline?.name}`}
        icon={CoursesIconComponent}
        meta={
          <Flex>
            <Tag>{discipline?.code}</Tag>
          </Flex>
        }
        actions={
          canClone?.can && (
            <Button
              type="default"
              icon={<CopyOutlined />}
              onClick={() => setCloneModalOpen(true)}
            >
              Clonar disciplina
            </Button>
          )
        }
      />

      <Tabs
        defaultActiveKey={activeTab}
        onChange={activeKey => setActiveTab(activeKey)}
        className="custom-tabs"
      >
        {canEdit?.can && (
          <Tabs.TabPane tab="Informações gerais" key="info">
            <Form
              {...formProps}
              initialValues={{
                ...queryResult?.data?.data,
                code: queryResult?.data?.data?.code?.slice(3)
              }}
              onFinish={onFinish}
              layout="vertical"
            >
              <Form.Item
                label="Código da disciplina"
                name="code"
                rules={[
                  {
                    required: true
                  },
                  {
                    pattern: new RegExp(/^\d{4}$/),
                    message: 'Insira um numero válido de 4 digitos'
                  }
                ]}
              >
                <Input
                  className="max-w-[110px]"
                  maxLength={4}
                  addonBefore={name?.slice(0, 3)?.toUpperCase()}
                />
              </Form.Item>
              <Form.Item
                label="Nome"
                name="name"
                rules={[
                  {
                    required: true
                  }
                ]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                label="Duração (minutos)"
                name="duration_in_minutes"
                rules={[
                  {
                    required: true
                  }
                ]}
              >
                <Input type="number" />
              </Form.Item>
              <Form.Item label="Modulos" name="module_ids">
                <Select
                  {...modulesSelectProps}
                  options={modules}
                  mode="multiple"
                />
              </Form.Item>
            </Form>
          </Tabs.TabPane>
        )}
        {canEdit?.can && (
          <Tabs.TabPane tab="Estrutura" key="tree">
            {renderUpdateStatusAlert(classroomsUpdateStatus, () =>
              setProgressModalOpen(true)
            )}

            {discipline && (
              <>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    rowGap: 8,
                    marginBottom: 16
                  }}
                >
                  <div>
                    <Typography.Title level={5} style={{ marginBottom: 0 }}>
                      Estrutura da disciplina
                    </Typography.Title>
                    <Typography.Text type="secondary">
                      As alterações são aplicadas às novas turmas. Para
                      atualizar turmas já existentes, clicar em atualizar turmas
                      e escolher.
                    </Typography.Text>
                  </div>

                  <div style={{ display: 'flex', gap: 8 }}>
                    <Button type="default" icon={<HistoryOutlined />}>
                      Histórico
                    </Button>

                    <Button
                      type="default"
                      onClick={
                        classroomsUpdateStatus.total === 0 ||
                        classroomsUpdateStatus.done
                          ? () => setUpdateModalOpen(true)
                          : () => setProgressModalOpen(true)
                      }
                      icon={<UnorderedListOutlined />}
                    >
                      {classroomsUpdateStatus.total > 0 &&
                      !classroomsUpdateStatus.done
                        ? 'Atualização em andamento'
                        : 'Atualizar turmas'}
                    </Button>
                  </div>
                </div>

                <Tree
                  unit={undefined}
                  tree={contentTree || discipline.content_tree}
                  onUpdate={onUpdateTree}
                />
              </>
            )}
          </Tabs.TabPane>
        )}
        {canListClassrooms?.can && (
          <Tabs.TabPane tab="Turmas" key="classrooms">
            <TabClassrooms />
          </Tabs.TabPane>
        )}
      </Tabs>
      <ClassroomsDisciplineUpdateModal
        open={updateModalOpen}
        onCancel={() => setUpdateModalOpen(false)}
        onUpdate={() => {
          setUpdateModalOpen(false);
          setProgressModalOpen(true);
        }}
      />

      <UpdateProgressModal
        open={progressModalOpen}
        onClose={() => setProgressModalOpen(false)}
        updateStatus={classroomsUpdateStatus!}
      />

      <Modal
        title="Clonar disciplina"
        open={cloneModalOpen}
        okText="Clonar"
        cancelText="Cancelar"
        confirmLoading={isCloning}
        onOk={() => cloneForm.submit()}
        onCancel={() => {
          setCloneModalOpen(false);
          cloneForm.resetFields();
        }}
      >
        <Form
          form={cloneForm}
          layout="vertical"
          onFinish={handleClone}
          initialValues={{
            name: discipline?.name ? `${discipline.name} (Copia)` : undefined
          }}
        >
          <Form.Item
            label="Código da disciplina"
            name="code"
            rules={[
              { required: true },
              {
                pattern: new RegExp(/^\d{4}$/),
                message: 'Insira um numero válido de 4 digitos'
              }
            ]}
          >
            <Input
              className="max-w-[110px]"
              maxLength={4}
              addonBefore={tenantPrefix}
            />
          </Form.Item>
          <Form.Item label="Nome" name="name">
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </EditLayout>
  );
};

const renderUpdateStatusAlert = (
  status: IClassroomUpdateStatus,
  onClick: () => void
) => {
  if (!status || (!status.done && !status.total)) {
    return null;
  }

  // In progress
  if (status.total > 0 && status.done === false) {
    return (
      <Alert
        message="Atualização em andamento"
        description={
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <span>As alterações estão sendo aplicadas às turmas.</span>
            <Button onClick={onClick} style={{ marginLeft: 24 }}>
              Visualizar
            </Button>
          </div>
        }
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />
    );
  }

  // Success
  if (
    status.done &&
    status.failed_classrooms.length === 0 &&
    status.total > 0
  ) {
    return (
      <Alert
        description={
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <span>Processo das turmas atualizado com sucesso</span>
            <Button onClick={onClick}>Visualizar</Button>
          </div>
        }
        type="success"
        showIcon
        style={{ marginBottom: 16 }}
      />
    );
  }

  // Error (optional, if you want to handle error states)
  if (status.done && status.total === status.failed_classrooms.length) {
    return (
      <Alert
        description={
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <span>Não foi possível realizar a atualização das turmas</span>
            <Button onClick={onClick}>Visualizar</Button>
          </div>
        }
        type="error"
        showIcon
        style={{ marginBottom: 16 }}
      />
    );
  }

  // Partial (warning)
  if (status.done && status.failed_classrooms.length > 0) {
    return (
      <Alert
        description={
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <span>
              Processo de atualização das turmas realizado com algumas
              observações
            </span>
            <Button onClick={onClick}>Visualizar</Button>
          </div>
        }
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
      />
    );
  }

  return null;
};
